import React, { useRef, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Sky, FlyControls } from '@react-three/drei';
import { EffectComposer, Bloom, Vignette, ToneMapping, SMAA } from '@react-three/postprocessing';
import { ToneMappingMode } from 'postprocessing';
import { useControls, folder } from 'leva';
import * as THREE from 'three';
import Clouds from './Clouds';
import ProceduralOcean from './ProceduralOcean';
import OceanFloor from './OceanFloor';
import GodRays from './GodRays';

function CameraClamp({ controlsRef, floorDepth, lockAltitude }) {
  const { camera } = useThree();
  const lockedY = useRef(camera.position.y);
  const originalUpdate = useRef(null);

  React.useLayoutEffect(() => {
    const controls = controlsRef.current;
    if (!controls) return;

    if (lockAltitude) {
      lockedY.current = camera.position.y;
      originalUpdate.current = controls.update;
      controls.update = function (delta) {
        originalUpdate.current.call(this, delta);
        camera.position.y = lockedY.current;
      };
      return () => {
        controls.update = originalUpdate.current;
      };
    }
  }, [lockAltitude, camera, controlsRef]);

  useFrame(({ camera }) => {
    if (!lockAltitude) {
      const minY = floorDepth + 2;
      if (camera.position.y < minY) {
        camera.position.y = minY;
      }
    }
  });
  return null;
}

function UnderwaterEffect({ seaHeight, waterClarity }) {
  const { scene } = useThree();
  const fogRef = useRef(new THREE.Fog(0x001a2e, 1, 30));
  const bgColorRef = useRef(new THREE.Color());

  useFrame(({ camera }) => {
    if (camera.position.y < seaHeight) {
      const depth = seaHeight - camera.position.y;

      // waterClarity controls visibility range: 0=murky(10u), 1=clear(60u)
      const visFar = 10 + waterClarity * 50;
      const visNear = visFar * 0.1;
      fogRef.current.near = visNear;
      fogRef.current.far = visFar;

      // Wavelength-dependent fog color: deeper = darker blue
      const depthFactor = Math.exp(-depth * 0.08);
      const r = 0.01 * depthFactor;
      const g = 0.08 * depthFactor + 0.04;
      const b = 0.15 * depthFactor + 0.06;
      bgColorRef.current.setRGB(r, g, b);
      fogRef.current.color.copy(bgColorRef.current);

      scene.fog = fogRef.current;
      scene.background = bgColorRef.current;
    } else {
      scene.fog = null;
      scene.background = null;
    }
  });

  return null;
}

function App() {
  const [{ coverage, cloudDensity, windSpeed, cloudBase, cloudTop, cloudOpacity, azimuth, inclination, rayleigh, speed, drag, forward, lockAltitude, seaSpeed, seaHeight, seaChoppy, seaFreq, normalStrength, sssIntensity, foamAmount, floorDepth, floorBrightness, causticIntensity, waterClarity, godRaysEnabled }, set] = useControls(() => ({
    Clouds: folder({
      coverage: { value: 0.16, min: 0.0, max: 1.0, step: 0.01 },
      cloudDensity: { value: 0.32, min: 0.001, max: 0.5, step: 0.001 },
      windSpeed: { value: 3.20, min: 0, max: 10, step: 0.01 },
      cloudBase: { value: 400, min: 50, max: 10000, step: 50 },
      cloudTop: { value: 5000, min: 100, max: 20000, step: 50 },
      cloudOpacity: { value: 0.90, min: 0.0, max: 1.0, step: 0.01 },
    }),
    Sky: folder({
      azimuth: { value: 0.30, min: 0, max: 1, step: 0.001 },
      inclination: { value: 0.49, min: 0, max: 0.5, step: 0.001 },
      rayleigh: { value: 3, min: 0, max: 4, step: 0.001 },
    }),
    Ocean: folder({
      seaSpeed: { value: 0.8, min: 0.0, max: 3.0, step: 0.01 },
      seaHeight: { value: 0.6, min: 0.0, max: 3.0, step: 0.01 },
      seaChoppy: { value: 2.0, min: 0.0, max: 6.0, step: 0.1 },
      seaFreq: { value: 0.16, min: 0.01, max: 1.0, step: 0.01 },
      normalStrength: { value: 0.4, min: 0.0, max: 2.0, step: 0.01 },
      sssIntensity: { value: 1.2, min: 0.0, max: 5.0, step: 0.1 },
      foamAmount: { value: 0.6, min: 0.0, max: 2.0, step: 0.01 },
    }),
    Underwater: folder({
      floorDepth: { value: -5, min: -100, max: -1, step: 1 },
      floorBrightness: { value: 0.7, min: 0.0, max: 2.0, step: 0.05 },
      causticIntensity: { value: 1.5, min: 0.0, max: 5.0, step: 0.1 },
      waterClarity: { value: 0.5, min: 0.0, max: 1.0, step: 0.01 },
      godRaysEnabled: { value: true },
    }),
    Controls: folder({
      speed: { value: 0.5, min: 0.1, max: 2, step: 0.1 },
      drag: { value: false },
      forward: { value: true },
      lockAltitude: { value: true, label: 'Lock Altitude' },
    }),
  }));

  useEffect(() => {
    const handler = (e) => set(e.detail);
    window.addEventListener('tuniverse-settings', handler);
    return () => window.removeEventListener('tuniverse-settings', handler);
  }, [set]);

  const sunPosition = React.useMemo(() => {
    const phi = Math.PI * (0.5 - inclination);
    const theta = 2 * Math.PI * (azimuth - 0.5);
    return [
      Math.cos(phi) * Math.cos(theta),
      Math.sin(phi),
      Math.cos(phi) * Math.sin(theta)
    ];
  }, [azimuth, inclination]);

  const cameraRef = useRef();

  return (
    <Canvas
      camera={{
        fov: 75,
        near: 0.1,
        far: 10000,
        position: [0, 1, 5]
      }}
      gl={{
        toneMapping: THREE.NoToneMapping,
        toneMappingExposure: 0.5,
      }}
    >
      <ambientLight intensity={5} />
      <Sky
        distance={450000}
        sunPosition={sunPosition}
        inclination={inclination}
        azimuth={azimuth}
        turbidity={10}
        rayleigh={rayleigh}
        mieCoefficient={0.005}
        mieDirectionalG={0.7}
      />
      <Clouds
        sunPosition={sunPosition}
        coverage={coverage}
        cloudDensity={cloudDensity}
        windSpeed={windSpeed}
        cloudBase={cloudBase}
        cloudTop={cloudTop}
        cloudOpacity={cloudOpacity}
      />

      <UnderwaterEffect seaHeight={seaHeight} waterClarity={waterClarity} />
      <ProceduralOcean
        sunPosition={sunPosition}
        seaSpeed={seaSpeed}
        seaHeight={seaHeight}
        seaChoppy={seaChoppy}
        seaFreq={seaFreq}
        normalStrength={normalStrength}
        sssIntensity={sssIntensity}
        foamAmount={foamAmount}
      />
      <OceanFloor
        sunPosition={sunPosition}
        seaSpeed={seaSpeed}
        seaHeight={seaHeight}
        seaChoppy={seaChoppy}
        seaFreq={seaFreq}
        floorDepth={floorDepth}
        floorBrightness={floorBrightness}
        causticIntensity={causticIntensity}
        waterClarity={waterClarity}
      />
      <GodRays
        sunPosition={sunPosition}
        enabled={godRaysEnabled}
      />

      <FlyControls
        ref={cameraRef}
        movementSpeed={speed}
        rollSpeed={0}
        dragToLook={drag}
        autoForward={forward}
      />
      <CameraClamp controlsRef={cameraRef} floorDepth={floorDepth} lockAltitude={lockAltitude} />

      <EffectComposer multisampling={0}>
        <SMAA />
        <Bloom luminanceThreshold={0.9} intensity={0.3} luminanceSmoothing={0.025} mipmapBlur radius={0.4} />
        <ToneMapping mode={ToneMappingMode.ACES_FILMIC} />
        <Vignette offset={0.3} darkness={0.4} />
      </EffectComposer>
    </Canvas>
  );
}

export default App;
